import { useState, useEffect, useRef, useCallback } from "react";

/* ─── inline CSS for things Tailwind can't do ───────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap');

  .sponsa-font { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }

  /* body-level bg glow — apply this class to your page wrapper */
  .sponsa-bg {
    background: #030021;
    min-height: 100vh;
    position: relative;
  }
  .sponsa-bg::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse at 30% 20%, rgba(0,255,143,0.04) 0%, transparent 50%),
      radial-gradient(ellipse at 70% 80%, rgba(168,85,247,0.03) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  /* Nav link hover */
  .sponsa-nav-link {
    font-size: 13px;
    font-weight: 500;
    color: rgba(255,255,255,0.6);
    text-decoration: none;
    padding: 8px 14px;
    border-radius: 8px;
    transition: color 0.2s ease, background 0.2s ease;
    white-space: nowrap;
  }
  .sponsa-nav-link:hover {
    color: #ffffff;
    background: rgba(255,255,255,0.06);
  }

  /* Log In button */
  .sponsa-btn-login {
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: rgba(255,255,255,0.6);
    background: none;
    border: 1px solid rgba(255,255,255,0.08);
    padding: 7px 18px;
    border-radius: 8px;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.2s ease, border-color 0.2s ease, background 0.2s ease;
    white-space: nowrap;
    align-items: center;
    justify-content: center;
  }
  .sponsa-btn-login:hover {
    color: #ffffff;
    border-color: rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.06);
  }

  /* Sign Up button */
  .sponsa-btn-signup {
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    color: #030021;
    background: #00FF8F;
    border: none;
    padding: 8px 20px;
    border-radius: 8px;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.2s ease, box-shadow 0.2s ease;
    white-space: nowrap;
    align-items: center;
    justify-content: center;
  }
  .sponsa-btn-signup:hover {
    background: rgba(0,255,143,0.85);
    box-shadow: 0 4px 20px rgba(0,255,143,0.25);
  }

  /* Mobile menu link */
  .sponsa-mobile-link {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255,255,255,0.6);
    text-decoration: none;
    padding: 12px 14px;
    border-radius: 10px;
    transition: color 0.15s ease, background 0.15s ease;
    display: block;
  }
  .sponsa-mobile-link:hover {
    color: #ffffff;
    background: rgba(255,255,255,0.06);
  }

  /* Mobile menu panel animation */
  .sponsa-mobile-menu {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    background: rgba(3,0,33,0.95);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255,255,255,0.08);
    padding: 8px 16px 16px;
    flex-direction: column;
    gap: 4px;
    z-index: 999;
    transform-origin: top;
    transition: opacity 0.25s ease, transform 0.25s ease, visibility 0.25s;
  }
  .sponsa-mobile-menu[data-open="false"] {
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
  }
  .sponsa-mobile-menu[data-open="true"] {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  /* Hamburger button */
  .sponsa-hamburger {
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    color: #ffffff;
    cursor: pointer;
    
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: background 0.2s ease;
    flex-shrink: 0;
  }
  .sponsa-hamburger:hover { background: rgba(255,255,255,0.06); }
`;

/* ─── SPONSA SVG logo icon ───────────────────────────────────────────────── */
function SponSaIcon({ size = 32 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="375 478 215 245"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient
          id="slg1"
          gradientUnits="userSpaceOnUse"
          x1="393.67"
          y1="684.95"
          x2="533.85"
          y2="544.05"
        >
          <stop offset="0" stopColor="#EF237C" />
          <stop offset="1" stopColor="#FFD50E" />
        </linearGradient>
        <linearGradient
          id="slg2"
          gradientUnits="userSpaceOnUse"
          x1="440.92"
          y1="745.23"
          x2="552.7"
          y2="633.44"
        >
          <stop offset="0" stopColor="#EF237C" />
          <stop offset="1" stopColor="#FFD50E" />
        </linearGradient>
        <linearGradient
          id="slg4"
          gradientUnits="userSpaceOnUse"
          x1="523.51"
          y1="453.91"
          x2="392.39"
          y2="584.13"
        >
          <stop offset="0" stopColor="#00A1FF" />
          <stop offset="1" stopColor="#00FF8F" />
        </linearGradient>
        <linearGradient
          id="slg5"
          gradientUnits="userSpaceOnUse"
          x1="541.26"
          y1="501.88"
          x2="417.71"
          y2="676.55"
        >
          <stop offset="0" stopColor="#00A1FF" />
          <stop offset="1" stopColor="#00FF8F" />
        </linearGradient>
      </defs>
      <path
        fill="url(#slg1)"
        d="M572.12,577.15L407.87,717.12l67.91-186.27h15.84c9.4,0,18.48,1.39,27.04,3.99C541.36,541.72,560.4,557.04,572.12,577.15z"
      />
      <path
        fill="url(#slg2)"
        d="M584.74,623.98c0,0.28,0,0.56-0.01,0.83c-0.45,51.05-41.97,92.3-93.13,92.3h-83.74l164.25-139.97c0.97,1.66,1.89,3.35,2.76,5.08C581.19,594.79,584.74,608.97,584.74,623.98z"
      />
      <path
        fill="url(#slg4)"
        d="M555.4,482.88L391.14,622.85c-0.97-1.66-1.89-3.35-2.76-5.08c-6.31-12.56-9.87-26.74-9.87-41.76c0-0.28,0-0.56,0.01-0.83c0.45-51.05,41.97-92.3,93.13-92.3H555.4z"
      />
      <path
        fill="url(#slg5)"
        d="M555.4,482.88l-67.91,186.27h-15.84c-9.4,0-18.48-1.39-27.04-3.99c-22.72-6.88-41.75-22.2-53.48-42.31L555.4,482.88z"
      />
    </svg>
  );
}

/* ─── Hamburger / Close icon ─────────────────────────────────────────────── */
function HamburgerIcon({ open }) {
  return open ? (
    <svg
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  ) : (
    <svg
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

export default function NewLandingHeader({
  navLinks = [
    { label: "Features", href: "/#features" },
    { label: "How It Works", href: "/#how-it-works" },
    { label: "FAQ", href: "/#faqs" },
    { label: "Explore", href: "/explore" },
  ],
  onLogin,
  onSignup,
  loginHref = "/account",
  signupHref = "/signup/",
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  /* ── Close on outside click ── */
  const handleOutsideClick = useCallback((e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setMenuOpen(false);
    }
  }, []);

  /* ── Close on Escape ── */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") setMenuOpen(false);
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.addEventListener("click", handleOutsideClick);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("click", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen, handleOutsideClick, handleKeyDown]);

  /* ── Close on nav link click ── */
  const handleNavLinkClick = () => setMenuOpen(false);

  return (
    <>
      <style>{css}</style>

      {/* ── Fixed Header Bar ── */}
      <header
        className="sponsa-font fixed top-0 left-0 right-0 z-[1000] flex items-center justify-between px-6"
        style={{
          height: "60px",
          background: "rgba(3,0,33,0.95)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* ── Left: Logo ── */}
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center gap-[10px] no-underline">
            <SponSaIcon size={32} />
            <span className="text-[18px] font-bold text-white tracking-[-0.02em]">
              SPONSA
            </span>
            {/* BETA badge — superscript style */}
            <span
              className="text-[9px] font-bold uppercase tracking-[0.05em] rounded-[4px] px-[6px] py-[4px] leading-none -ml-1 -mt-3 inline-flex items-center justify-center"
              style={{ color: "#030021", background: "#00FF8F" }}
            >
              BETA
            </span>
          </a>
        </div>

        {/* ── Centre: Desktop Nav ── */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <a key={link.label} href={link.href} className="sponsa-nav-link">
              {link.label}
            </a>
          ))}
        </nav>

        {/* ── Right: Auth buttons + Hamburger ── */}
        <div className="flex items-center gap-[10px]">
          {/* Desktop auth buttons */}
          <a
            href={loginHref}
            className="sponsa-btn-login hidden md:inline-flex"
            onClick={onLogin}
          >
            Log In
          </a>
          <a
            href={signupHref}
            className="sponsa-btn-signup hidden md:inline-flex"
            onClick={onSignup}
          >
            Sign Up
          </a>

          {/* Mobile hamburger */}
          <button
            className="sponsa-hamburger md:hidden flex  "
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            <HamburgerIcon open={menuOpen} />
          </button>
        </div>
      </header>

      {/* ── Mobile Dropdown Menu ── */}
      <div
        ref={menuRef}
        className="sponsa-mobile-menu sponsa-font md:hidden flex"
        data-open={String(menuOpen)}
        onClick={(e) => e.stopPropagation()}
      >
        {navLinks.map((link) => (
          <a
            key={link.label}
            href={link.href}
            className="sponsa-mobile-link"
            onClick={handleNavLinkClick}
          >
            {link.label}
          </a>
        ))}
        {/* Divider */}
        <div
          className="my-[6px]"
          style={{ height: "1px", background: "rgba(255,255,255,0.08)" }}
        />

        {/* Mobile auth row */}
        <div className="flex gap-[10px] px-[14px] py-[6px]">
          <a
            href={loginHref}
            className="sponsa-btn-login flex-1 flex"
            onClick={() => {
              handleNavLinkClick();
              onLogin?.();
            }}
          >
            Log In
          </a>
          <a
            href={signupHref}
            className="sponsa-btn-signup flex-1 flex"
            onClick={() => {
              handleNavLinkClick();
              onSignup?.();
            }}
          >
            Sign Up
          </a>
        </div>
      </div>
    </>
  );
}
